import {createAttioCompany} from '@/helpers/attio/attio.server';
import {Organizer, OrganizerDetail} from '@/apiTypes/event';
import {Event} from '@/apiTypes/event';
import {CreateOrganizerInput} from '@/apiTypes/organizer';
import {authMiddleware, optionalAuthMiddleware} from '@/helpers/authMiddleware';
import {prismaClient} from '@/helpers/prismaClient.server';
import {requireOrganizerAccess} from '@/helpers/requireAuth.server';
import {extractRequestContext, trackEvent} from '@/helpers/mixpanel.server';
import {Prisma} from '@wayra/database';
import {createServerFn} from '@tanstack/react-start';
import {getRequest} from '@tanstack/react-start/server';
import {z} from 'zod';

const GetOrganizerEventsInput = z.object({
  organizerId: z.string().uuid(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(12),
});

/**
 * Fetches an organizer by ID with computed stats:
 * total event count and total participant count (aggregated
 * from category participantCount fields across all events).
 */
export const getOrganizerById = createServerFn({method: 'GET'})
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID format');
    }
    return result.data;
  })
  .handler(async ({data: organizerId}) => {
    const db = await prismaClient();

    const organizer = await db.organizer.findUnique({
      where: {id: organizerId},
    });

    if (!organizer) {
      throw new Error('Organizer not found');
    }

    const [totalEvents, totalParticipants, totalAdmins] = await Promise.all([
      db.event.count({where: {organizerId, archived: false}}),
      db.registration.count({
        where: {event: {organizerId}, status: {in: ['APPROVED', 'PAID']}},
      }),
      db.organizerAdmin.count({
        where: {organizerId},
      }),
    ]);

    return OrganizerDetail.parse({
      ...organizer,
      totalEvents,
      totalParticipants,
      totalAdmins,
    });
  });

/**
 * Fetches paginated upcoming events for a given organizer,
 * ordered by start date ascending (upcoming first).
 */
export const getOrganizerEvents = createServerFn({method: 'GET'})
  .inputValidator((data: unknown): z.infer<typeof GetOrganizerEventsInput> => {
    const result = GetOrganizerEventsInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid input');
    }
    return result.data;
  })
  .handler(async ({data}) => {
    const {organizerId, page, pageSize} = data;
    const skip = (page - 1) * pageSize;
    const db = await prismaClient();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const whereClause = {
      organizerId,
      archived: false,
      OR: [
        {endDate: {gte: today}},
        {endDate: null, startDate: {gte: today}},
        {endDate: null, startDate: null},
      ],
    };

    const [events, totalCount] = await db.$transaction([
      db.event.findMany({
        where: whereClause,
        skip,
        take: pageSize,
        orderBy: {startDate: {sort: 'asc', nulls: 'last'}},
        include: {
          routes: {
            select: {
              id: true,
              createdAt: true,
              updatedAt: true,
              name: true,
              description: true,
              distance_meters: true,
              elevation_gain_meters: true,
              routeFileUrl: true,
              eventId: true,
            },
          },
          categories: {
            include: {
              prices: true,
            },
          },
        },
      }),
      db.event.count({where: whereClause}),
    ]);

    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      events: z.array(Event).parse(events),
      pagination: {
        page,
        pageSize,
        totalCount,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      },
    };
  });

const GetDashboardEventsInput = z.object({
  organizerId: z.string().uuid(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(20),
  sortBy: z
    .enum(['name', 'eventType', 'startDate', 'totalParticipants'])
    .default('startDate'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  timeFilter: z.enum(['upcoming', 'past', 'archived']).default('upcoming'),
});

/**
 * Builds the Prisma `where` clause for the organizer dashboard event list.
 * Upcoming includes events whose end (or start, when no end) is today or
 * later, plus events with no dates set (so drafts stay visible). Past is
 * the inverse of upcoming, excluding date-less drafts. Archived returns
 * all archived events regardless of date. The day boundary is the start
 * of "today" in the server process's local time.
 */
function buildDashboardEventsWhere(
  organizerId: string,
  timeFilter: 'upcoming' | 'past' | 'archived'
) {
  if (timeFilter === 'archived') {
    return {organizerId, archived: true};
  }

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  if (timeFilter === 'upcoming') {
    return {
      organizerId,
      archived: false,
      OR: [
        {endDate: {gte: startOfToday}},
        {endDate: null, startDate: {gte: startOfToday}},
        {endDate: null, startDate: null},
      ],
    };
  }

  return {
    organizerId,
    archived: false,
    OR: [
      {endDate: {lt: startOfToday}},
      {endDate: null, startDate: {lt: startOfToday}},
    ],
  };
}

/**
 * Fetches paginated events for an organizer dashboard with configurable
 * sorting. Supports sorting by name, type, date, or participant count.
 */
export const getDashboardOrganizerEvents = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof GetDashboardEventsInput> => {
    const result = GetDashboardEventsInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid input');
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    await requireOrganizerAccess(data.organizerId, context.auth);
    const {organizerId, page, pageSize, sortBy, sortOrder, timeFilter} = data;
    const skip = (page - 1) * pageSize;
    const db = await prismaClient();

    const whereClause = buildDashboardEventsWhere(organizerId, timeFilter);

    const orderBy =
      sortBy === 'totalParticipants'
        ? {registrations: {_count: sortOrder}}
        : sortBy === 'startDate'
          ? {startDate: {sort: sortOrder, nulls: 'last'}}
          : {[sortBy]: sortOrder};

    const [events, totalCount] = await db.$transaction([
      db.event.findMany({
        where: whereClause,
        skip,
        take: pageSize,
        orderBy,
        include: {
          _count: {select: {registrations: true}},
        },
      }),
      db.event.count({where: whereClause}),
    ]);

    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      events: events.map((e) => ({
        id: e.id,
        name: e.name,
        eventType: e.eventType,
        startDate: e.startDate,
        endDate: e.endDate,
        location: e.location,
        archived: e.archived,
        totalParticipants: e._count.registrations,
      })),
      pagination: {
        page,
        pageSize,
        totalCount,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1,
      },
    };
  });

/**
 * Creates a new organizer and associates it with the authenticated user as owner.
 * Wraps both operations in a transaction for atomicity.
 */
export const createOrganizer = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof CreateOrganizerInput> => {
    const result = CreateOrganizerInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();
    const isSuperAdmin = profile.level === 'superadmin';

    try {
      const result = await db.$transaction(async (tx) => {
        const organizer = await tx.organizer.create({
          data: {
            name: data.name,
            description: data.description || null,
            website: data.website || null,
            contactEmail: data.contactEmail || null,
          },
        });

        // If the user is not a superadmin,
        // and the organization was not marked as unclaimed
        if (!(isSuperAdmin && data.markUnclaimed)) {
          await tx.organizerAdmin.create({
            data: {
              profileId: profile.id,
              organizerId: organizer.id,
            },
          });
        }
        return organizer;
      });

      if (!isSuperAdmin) {
        const attioResult = await createAttioCompany({
          name: data.name,
          description: data.description,
          domains: data.website ? [data.website] : undefined,
          creator: profile,
        });

        if (attioResult) {
          await db.organizer.update({
            where: {id: result.id},
            data: {attioCompanyId: attioResult.companyId},
          });

          if (attioResult.personId) {
            await db.profile.update({
              where: {id: profile.id},
              data: {attioPersonId: attioResult.personId},
            });
          }
        }
      }

      const request = getRequest();
      const requestContext = request
        ? extractRequestContext(request)
        : undefined;
      await trackEvent(
        profile.id,
        'Organization Created',
        {
          organization_id: result.id,
          organization_name: result.name,
        },
        requestContext
      );

      return {organizer: Organizer.parse(result)};
    } catch (err) {
      if (
        err instanceof Prisma.PrismaClientKnownRequestError &&
        err.code === 'P2002' &&
        Array.isArray(err.meta?.target) &&
        err.meta.target.includes('name')
      ) {
        throw new Error('An organizer with this name already exists');
      }
      throw err;
    }
  });

/**
 * Removes all OrganizerAdmin records from an organizer, resetting it
 * to "unclaimed" status so that events under it become claimable.
 * Restricted to superadmins.
 */
export const markOrganizerUnclaimed = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID format');
    }
    return result.data;
  })
  .handler(async ({data: organizerId, context}) => {
    const {profile} = context.auth;
    if (profile.level !== 'superadmin') {
      throw new Error('Only superadmins can mark organizers as unclaimed');
    }

    const db = await prismaClient();

    const organizer = await db.organizer.findUnique({
      where: {id: organizerId},
    });
    if (!organizer) {
      throw new Error('Organizer not found');
    }

    const deletedMemberships = await db.organizerAdmin.deleteMany({
      where: {organizerId},
    });

    return {success: true, removedMembers: deletedMemberships.count};
  });

/**
 * Returns all organizers the authenticated user is a member of,
 * ordered by most recently joined first.
 */
export const getUserOrganizers = createServerFn({
  method: 'GET',
})
  .middleware([authMiddleware])
  .handler(async ({context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const memberships = await db.organizerAdmin.findMany({
      where: {profileId: profile.id},
      include: {organizer: true},
      orderBy: {createdAt: 'desc'},
    });

    return memberships.map((m) => ({
      id: m.id,
      organizer: Organizer.parse(m.organizer),
    }));
  });

/**
 * Returns events from all organizers the authenticated user is a member of,
 * filtered to events from the start of the current month onward.
 * Returns [] when unauthenticated.
 */
export const getProfileOrganizingEvents = createServerFn({
  method: 'GET',
})
  .middleware([optionalAuthMiddleware])
  .handler(async ({context}) => {
    const auth = context.auth;
    if (!auth) return [];

    const db = await prismaClient();

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const memberships = await db.organizerAdmin.findMany({
      where: {profileId: auth.profile.id},
      select: {organizerId: true},
    });

    if (memberships.length === 0) return [];

    const organizerIds = memberships.map((m) => m.organizerId);

    const events = await db.event.findMany({
      where: {
        organizerId: {in: organizerIds},
        archived: false,
        OR: [
          {endDate: {gte: startOfMonth}},
          {endDate: null, startDate: {gte: startOfMonth}},
        ],
      },
      orderBy: {startDate: 'asc'},
      select: {
        id: true,
        name: true,
        startDate: true,
        endDate: true,
        location: true,
        heroUrl: true,
        eventType: true,
        discipline: true,
        externalId: true,
        lat: true,
        lng: true,
      },
    });

    return events;
  });

const SearchOrganizersInput = z.object({
  query: z.string().trim().min(2),
});

/**
 * Searches organizers by name. Available to all authenticated users.
 * Returns only id, name, logoUrl, and isVerified fields.
 */
export const searchOrganizers = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown) => {
    const result = SearchOrganizersInput.safeParse(data);
    if (!result.success) {
      throw new Error('Invalid search parameters');
    }
    return result.data;
  })
  .handler(async ({data: {query}}) => {
    const db = await prismaClient();
    const organizers = await db.organizer.findMany({
      where: {name: {contains: query, mode: 'insensitive'}},
      omit: {stripeConnectId: true},
      orderBy: {name: 'asc'},
      take: 20,
    });
    return organizers;
  });

/**
 * Lightweight check returning whether the authenticated user
 * is associated with at least one organizer.
 * Superadmins always return true — they can create events for any organizer.
 */
export const checkUserHasOrganizer = createServerFn({
  method: 'GET',
})
  .middleware([authMiddleware])
  .handler(async ({context}) => {
    const {profile} = context.auth;

    if (profile.level === 'superadmin') {
      return {hasOrganizer: true, organizerId: null};
    }

    const db = await prismaClient();

    const membership = await db.organizerAdmin.findFirst({
      where: {profileId: profile.id},
      select: {organizerId: true},
    });

    return {
      hasOrganizer: !!membership,
      organizerId: membership?.organizerId ?? null,
    };
  });

/**
 * Checks whether the current user (if authenticated) is an admin
 * of the specified organizer. Returns false for unauthenticated users.
 */
export const checkOrganizerAdminStatus = createServerFn({
  method: 'GET',
})
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID format');
    }
    return result.data;
  })
  .handler(async ({data: organizerId, context}) => {
    const auth = context.auth;
    const db = await prismaClient();

    const adminCount = await db.organizerAdmin.count({
      where: {organizerId},
    });
    const isClaimable = adminCount === 0;

    if (!auth) {
      return {isAdmin: false, isClaimable};
    }

    const adminRow = await db.organizerAdmin.findFirst({
      where: {
        profileId: auth.profile.id,
        organizerId,
      },
      select: {id: true},
    });

    return {
      isAdmin: !!adminRow,
      isClaimable,
    };
  });

/**
 * Claims an unclaimed event's organizer by associating the current user
 * as an owner. Only succeeds if the organizer has no existing members.
 */
export const claimEventOrganizer = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid event ID format');
    }
    return result.data;
  })
  .handler(async ({data: eventId, context}) => {
    const {profile} = context.auth;
    const db = await prismaClient();

    const result = await db.$transaction(async (tx) => {
      const event = await tx.event.findUnique({
        where: {id: eventId},
        select: {
          organizer: {
            select: {
              id: true,
              name: true,
              website: true,
              attioCompanyId: true,
            },
          },
        },
      });

      if (!event?.organizer) {
        throw new Error('Event has no organizer');
      }

      const organizer = event.organizer;

      const isClaimable =
        (await tx.organizerAdmin.count({
          where: {organizerId: organizer.id},
        })) === 0;

      if (!isClaimable) {
        throw new Error('Event is not claimable');
      }

      await tx.organizerAdmin.create({
        data: {
          profileId: profile.id,
          organizerId: organizer.id,
        },
      });

      return organizer;
    });

    if (!result.attioCompanyId) {
      const attioResult = await createAttioCompany({
        name: result.name,
        domains: result.website ? [result.website] : undefined,
        creator: profile,
      });

      if (attioResult) {
        await db.organizer.update({
          where: {id: result.id},
          data: {attioCompanyId: attioResult.companyId},
        });

        if (attioResult.personId) {
          await db.profile.update({
            where: {id: profile.id},
            data: {attioPersonId: attioResult.personId},
          });
        }
      }
    }

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'Event Claimed',
      {
        event_id: eventId,
        organization_id: result.id,
      },
      requestContext
    );

    return {success: true};
  });

const UpdateOrganizerInput = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable(),
  website: z.string().nullable(),
  contactEmail: z.string().nullable(),
  logoUrl: z.string().nullable(),
});

/**
 * Updates an organizer's details. Requires membership or superadmin access.
 */
export const updateOrganizer = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): z.infer<typeof UpdateOrganizerInput> => {
    const result = UpdateOrganizerInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    await requireOrganizerAccess(data.id, context.auth);
    const db = await prismaClient();

    await db.organizer.update({
      where: {id: data.id},
      data: {
        name: data.name,
        description: data.description,
        website: data.website,
        contactEmail: data.contactEmail,
        logoUrl: data.logoUrl,
      },
    });

    return {success: true};
  });

const UpdateOrganizerCommissionInput = z.object({
  id: z.string().uuid(),
  commissionBps: z.number().int().min(0).max(10000),
});

/**
 * Updates an organizer's commission rate. Requires superadmin access.
 */
export const updateOrganizerCommission = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof UpdateOrganizerCommissionInput> => {
      const result = UpdateOrganizerCommissionInput.safeParse(data);
      if (!result.success) {
        throw new Error(result.error.errors.map((e) => e.message).join(', '));
      }
      return result.data;
    },
  )
  .handler(async ({data, context}) => {
    if (context.auth.profile.level !== 'superadmin') {
      throw new Error('Only superadmins can update commission rates');
    }
    const db = await prismaClient();

    await db.organizer.update({
      where: {id: data.id},
      data: {commissionBps: data.commissionBps},
    });

    return {success: true};
  });

/**
 * Checks whether the current user has edit access to an organizer.
 * Returns true for superadmins or members of the organizer.
 */
export const checkOrganizerEditAccess = createServerFn({method: 'GET'})
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID format');
    }
    return result.data;
  })
  .handler(async ({data: organizerId, context}) => {
    const auth = context.auth;
    if (!auth) {
      return {hasAccess: false};
    }

    if (auth.profile.level === 'superadmin') {
      return {hasAccess: true};
    }

    const db = await prismaClient();
    const membership = await db.organizerAdmin.findFirst({
      where: {
        profileId: auth.profile.id,
        organizerId,
      },
    });

    return {hasAccess: !!membership};
  });
